import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Users, Shield, Phone, ArrowRight, Facebook, Instagram, Linkedin, Twitter, Youtube, Globe } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { useSiteContent } from '@/context/SiteContentContext';
import DynamicIcon from '@/components/common/DynamicIcon';
import { useAnimateOnView } from '@/hooks/useAnimateOnView';
import { useCountUp } from '@/hooks/useCountUp';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { ConfigurableLayout } from '@/components/common/ConfigurableLayout';
import { PageSidebar } from '@/components/common/PageSidebar';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DynamicForm } from '@/components/common/DynamicForm';
import { usePageDesign } from '@/hooks/usePageDesign';

const StatItem: React.FC<{ value: number; suffix: string; label: string; delay: number }> = ({
  value, suffix, label, delay
}) => {
  const { ref, formattedCount } = useCountUp({ end: value, suffix, delay, duration: 2000 });
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-2">{formattedCount}</div>
      <div className="text-primary-foreground/80">{label}</div>
    </div>
  );
};

const TeamSection: React.FC = () => {
  const [activeDept, setActiveDept] = useState<string>('all');

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['public-team-members'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('team_members')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const departments = useMemo(() => {
    const depts = new Set<string>();
    teamMembers.forEach(m => { if (m.department) depts.add(m.department); });
    return Array.from(depts).sort();
  }, [teamMembers]);

  const filteredMembers = useMemo(() => {
    if (activeDept === 'all') return teamMembers;
    return teamMembers.filter(m => m.department === activeDept);
  }, [teamMembers, activeDept]);

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const socialIcons = [
    { key: 'facebook_url', icon: Facebook },
    { key: 'instagram_url', icon: Instagram },
    { key: 'linkedin_url', icon: Linkedin },
    { key: 'twitter_url', icon: Twitter },
    { key: 'youtube_url', icon: Youtube },
    { key: 'website_url', icon: Globe },
  ] as const;

  if (teamMembers.length === 0) return null;

  return (
    <section className="py-16 lg:py-24" aria-labelledby="team-heading">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 id="team-heading" className="font-heading text-3xl lg:text-4xl font-bold mb-4">
            Meet Our Team
          </h2>
          <p className="text-muted-foreground">The experienced professionals behind our success.</p>
        </div>

        {/* Department Tabs */}
        {departments.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <Button
              variant={activeDept === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveDept('all')}
            >
              All
            </Button>
            {departments.map(dept => (
              <Button
                key={dept}
                variant={activeDept === dept ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveDept(dept)}
              >
                {dept}
              </Button>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {filteredMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center bg-card p-6 rounded-xl border border-border hover:shadow-lg transition-shadow"
            >
              <Avatar className="w-24 h-24 mx-auto mb-4">
                <AvatarImage src={member.featured_image || ''} alt={member.featured_image_alt || member.full_name} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xl">
                  {getInitials(member.full_name)}
                </AvatarFallback>
              </Avatar>
              <h3 className="font-heading font-semibold text-lg">{member.full_name}</h3>
              {member.title && <p className="text-primary text-sm mb-1">{member.title}</p>}
              {member.department && <p className="text-xs text-muted-foreground mb-2">{member.department}</p>}
              {member.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{member.description}</p>}
              {/* Social links */}
              <div className="flex justify-center gap-3">
                {socialIcons.map(({ key, icon: Icon }) => {
                  const url = (member as any)[key];
                  if (!url) return null;
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const AboutPage: React.FC = () => {
  const { aboutTitle, aboutDescription, aboutHighlights, differentiators, services, template, seoCanonical } = useSiteContent();
  const storyRef = useAnimateOnView();
  const { heroStyle, layoutVariant, showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection, showLeadForm } = usePageDesign('about');

  const brandName = template.brand?.name || 'Our Company';
  const yearsInBusiness = template.stats?.yearsInBusiness || 10;

  // Related services for sidebar
  const serviceLinks = services.slice(0, 5).map(service => ({
    title: service.title,
    href: `/services#${service.slug}`,
    icon: service.icon,
  }));

  return (
    <>
      <SEOHead
        title={`About ${brandName} | ${yearsInBusiness}+ Years of Excellence`}
        description={`Learn about ${brandName}, a trusted local service provider with ${yearsInBusiness}+ years of experience. Meet our team and discover why customers choose us for quality workmanship.`}
        canonical={seoCanonical ? `${seoCanonical}/about` : undefined}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ConfigurableHero
          heroStyle={heroStyle}
          title={`About ${brandName}`}
          subtitle={template.brand?.tagline || `${yearsInBusiness}+ years of trusted service, quality workmanship, and customer satisfaction.`}
          badge={`Est. ${new Date().getFullYear() - yearsInBusiness}`}
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'About' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
          showRating
          rating={template.stats?.googleRating || 4.9}
          reviewCount={template.stats?.reviewCount || 100}
        />

        {/* Story & Mission with Sidebar */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <main className="lg:col-span-2" role="main">
                <motion.div
                  ref={storyRef.ref}
                  initial={{ opacity: 0, y: 20 }}
                  animate={storyRef.isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">Our Story</h2>
                  <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                    {aboutDescription || template.about?.story || ''}
                  </p>
                  
                  <div className="bg-muted p-6 rounded-xl border-l-4 border-primary mb-8">
                    <h3 className="font-heading font-semibold text-lg mb-2">Our Mission</h3>
                    <p className="text-muted-foreground">{template.about?.mission || ''}</p>
                  </div>

                  {/* Values Grid */}
                  <h3 className="font-heading text-2xl font-bold mb-6">Our Values</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {aboutHighlights.map((value, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <CheckCircle className="h-5 w-5 text-primary" />
                        </div>
                        <span className="font-medium">{value}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Why Choose Us */}
                  <h3 className="font-heading text-2xl font-bold mb-6">Why Choose Us</h3>
                  <div className="space-y-3">
                    {differentiators.map((reason, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">{reason.title}</span>
                          {reason.description && (
                            <p className="text-sm text-muted-foreground mt-0.5">{reason.description}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </main>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-28">
                  <PageSidebar 
                    showCta
                    showTrustBadges
                    showPromo
                    relatedLinks={serviceLinks}
                    relatedTitle="Our Services"
                    showServiceAreas
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-primary text-primary-foreground" aria-labelledby="stats-heading">
          <h2 id="stats-heading" className="sr-only">Company Statistics</h2>
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <StatItem value={template.stats?.yearsInBusiness || 10} suffix="+" label="Years in Business" delay={0} />
              <StatItem value={template.stats?.projectsCompleted || 500} suffix="+" label="Projects Completed" delay={200} />
              <StatItem value={template.stats?.reviewCount || 100} suffix="+" label="Happy Customers" delay={400} />
              <StatItem value={template.stats?.googleRating || 4.9} suffix="" label="Google Rating" delay={600} />
            </div>
          </div>
        </section>

        <TeamSection />

        {/* Dynamic Form assigned to About page */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="container mx-auto px-4 max-w-2xl">
            <DynamicForm pageSlug="about" />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-muted" aria-labelledby="about-cta-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 id="about-cta-heading" className="font-heading text-3xl font-bold mb-4">
                Ready to Work With Us?
              </h2>
              <p className="text-muted-foreground mb-8">
                Contact our team today to discuss your project and get a free estimate.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">
                    Get Free Estimate
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href={`tel:${template.contact?.phone?.replace(/\D/g, '') || ''}`}>
                    <Phone className="mr-2 h-4 w-4" />
                    {template.contact?.phone || 'Call Now'}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};
